import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAppContext } from "@/components/DolphinLayout";
import BrowserViewer from "@/pages/BrowserViewer";
import {
  Plus,
  Lightning,
  MagnifyingGlass,
  ArrowsClockwise,
  Funnel,
  DotsThree,
  Play,
  Stop,
  Trash,
  Copy,
  PencilSimple,
  WindowsLogo,
  AppleLogo,
  LinuxLogo,
  Globe,
  CaretLeft,
  CaretRight,
  Folder,
  FolderOpen,
  CheckSquare,
  Info,
  CreditCard,
  CaretDown,
  Rocket,
  ChatCircleText,
  DownloadSimple,
  ArrowsLeftRight,
  PencilSimpleLine,
  CircleNotch,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilesTable = () => {
  const { refreshStats, refreshFolders, folders, selectedFolderId } = useAppContext();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [launchingId, setLaunchingId] = useState(null);
  const [massLaunching, setMassLaunching] = useState(false);
  const [exportingDolphin, setExportingDolphin] = useState(false);
  const [dolphinFolders, setDolphinFolders] = useState([]);
  const [dolphinFoldersLoaded, setDolphinFoldersLoaded] = useState(false);
  const [customStatuses, setCustomStatuses] = useState([]);
  const [infoProfile, setInfoProfile] = useState(null);
  const [telegramConnected, setTelegramConnected] = useState(true);
  const [viewerProfile, setViewerProfile] = useState(null);
  const [supportRunning, setSupportRunning] = useState(null);

  useEffect(() => {
    fetchProfiles();
    fetchCustomStatuses();
    checkTelegramStatus();
  }, [selectedFolderId]);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/profiles`);
      setProfiles(res.data);
      refreshStats?.();
    } catch (e) {
      toast.error("Ошибка загрузки профилей");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomStatuses = async () => {
    try {
      const res = await axios.get(`${API}/custom-statuses`);
      setCustomStatuses(res.data);
    } catch (e) {
      console.error("Failed to load custom statuses");
    }
  };

  const checkTelegramStatus = async () => {
    try {
      const res = await axios.get(`${API}/telegram/user-auth-status`);
      setTelegramConnected(res.data.authenticated);
    } catch {
      setTelegramConnected(false);
    }
  };

  const handleLaunch = async (profile) => {
    setLaunchingId(profile.id);
    try {
      const res = await axios.post(`${API}/profiles/${profile.id}/launch`);
      if (res.data.success) {
        toast.success(`Браузер запущен: ${(res.data.settings_applied || []).join(", ")}`);
        setViewerProfile(profile);
      } else {
        if (res.data.proxy_error) {
          toast.error(`Прокси не работает — подсвечен красным`);
        } else {
          toast.error(res.data.error || "Ошибка запуска");
        }
      }
      fetchProfiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка запуска");
    }
    setTimeout(() => setLaunchingId(null), 1500);
  };

  const handleStop = async (profile) => {
    try {
      await axios.post(`${API}/profiles/${profile.id}/stop`);
      toast.success(`Профиль ${profile.name} остановлен`);
      fetchProfiles();
    } catch (e) {
      toast.error("Ошибка остановки профиля");
    }
  };

  const handleMassLaunch = async () => {
    if (selectedIds.length === 0) {
      toast.error("Выберите профили для запуска");
      return;
    }
    setMassLaunching(true);
    try {
      const res = await axios.post(`${API}/profiles/mass-launch`, {
        profile_ids: selectedIds,
      });
      const d = res.data;
      toast.success(`Запущено: ${d.launched}, ошибок: ${d.failed}`);
      fetchProfiles();
      setSelectedIds([]);
    } catch (e) {
      toast.error("Ошибка массового запуска");
    } finally {
      setMassLaunching(false);
    }
  };

  const handleMassStop = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await axios.post(`${API}/profiles/mass-stop`, {
        profile_ids: selectedIds,
      });
      toast.success(`Остановлено: ${res.data.stopped}`);
      fetchProfiles();
      setSelectedIds([]);
    } catch (e) {
      toast.error("Ошибка массовой остановки");
    }
  };

  const handleMassDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await axios.post(`${API}/profiles/mass-delete`, {
        profile_ids: selectedIds,
      });
      toast.success(`Удалено: ${res.data.deleted}`);
      fetchProfiles();
      refreshFolders?.();
      setSelectedIds([]);
    } catch (e) {
      toast.error("Ошибка массового удаления");
    }
  };

  const handleMassMove = async (folderId) => {
    if (selectedIds.length === 0) return;
    try {
      await axios.post(`${API}/profiles/mass-move`, {
        profile_ids: selectedIds,
        folder_id: folderId || null,
      });
      toast.success(`Перемещено: ${selectedIds.length}`);
      fetchProfiles();
      refreshFolders?.();
      setSelectedIds([]);
    } catch (e) {
      toast.error("Ошибка перемещения");
    }
  };

  const handleExportDolphin = async (folderId = null) => {
    if (selectedIds.length === 0) return;
    setExportingDolphin(true);
    try {
      const payload = { profile_ids: selectedIds };
      if (folderId) payload.folder_id = folderId;
      const res = await axios.post(`${API}/dolphin-anty/export`, payload);
      if (res.data.success) {
        toast.success(`Dolphin Anty: ${res.data.exported}/${res.data.total} exported`);
        const failed = res.data.results.filter(r => !r.success);
        if (failed.length > 0) {
          failed.forEach(f => toast.error(`${f.profile_name}: ${typeof f.error === 'string' ? f.error : JSON.stringify(f.error)}`));
        }
      } else {
        const errors = res.data.results?.map(r => r.error).filter(Boolean) || [];
        const errMsg = errors[0];
        toast.error(typeof errMsg === 'string' ? errMsg : "Export failed");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.msg || detail?.message || "Export error");
      toast.error(msg);
    } finally {
      setExportingDolphin(false);
    }
  };

  const fetchDolphinFolders = async () => {
    if (dolphinFoldersLoaded) return;
    try {
      const res = await axios.get(`${API}/dolphin-anty/folders`);
      setDolphinFolders(res.data.folders || []);
      setDolphinFoldersLoaded(true);
    } catch { /* */ }
  };

  const handleSupportScenario = async (profile) => {
    setSupportRunning(profile.id);
    try {
      // Start the scenario (returns immediately)
      const res = await axios.post(`${API}/profiles/${profile.id}/support-scenario`);
      if (res.data.proxy_error) {
        toast.error("Прокси не работает");
        setSupportRunning(null);
        return;
      }
      if (!res.data.success && !res.data.started) {
        toast.error(res.data.error || "Ошибка запуска Cashinout");
        setSupportRunning(null);
        return;
      }

      // Show the browser viewer
      setViewerProfile(profile);
      toast.info("Cashinout запущен...", { duration: 3000 });

      // Poll for status
      const pollInterval = 3000;
      const maxPolls = 120; // 6 minutes max
      let polls = 0;

      const poll = async () => {
        polls++;
        try {
          const statusRes = await axios.get(`${API}/profiles/${profile.id}/support-scenario/status`);
          const { status, steps, result } = statusRes.data;

          if (status === "done" && result) {
            const action = result.action || "";
            let msg;
            if (action === "screenshots_uploaded") {
              msg = `Cashinout: текст + скриншоты (${result.files_count || 0} шт)`;
            } else if (action === "text_sent") {
              msg = "Cashinout: текст отправлен";
            } else if (action === "no_action") {
              msg = "Cashinout: триггер не найден в чате";
            } else {
              msg = `Cashinout выполнен. Шагов: ${(steps || []).length}`;
            }
            toast.success(msg, { duration: 5000, description: (steps || []).slice(-3).join(" → ") });
            fetchProfiles();
            setSupportRunning(null);
            return;
          }

          if (status === "error") {
            const errMsg = result?.error || "Ошибка Cashinout";
            toast.error(errMsg, { duration: 8000, description: (steps || []).slice(-3).join(" → ") });
            setSupportRunning(null);
            return;
          }

          if (polls >= maxPolls) {
            toast.error("Cashinout: превышено время ожидания");
            setSupportRunning(null);
            return;
          }

          // Still running — update progress toast
          if (steps && steps.length > 0) {
            const lastStep = steps[steps.length - 1];
            toast.info(`Cashinout: ${lastStep}`, { id: "cashinout-progress", duration: pollInterval + 1000 });
          }

          setTimeout(poll, pollInterval);
        } catch {
          if (polls < maxPolls) {
            setTimeout(poll, pollInterval);
          } else {
            toast.error("Cashinout: ошибка соединения");
            setSupportRunning(null);
          }
        }
      };

      setTimeout(poll, pollInterval);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка Cashinout");
      setSupportRunning(null);
    }
  };

  const [dolphinRunning, setDolphinRunning] = useState(null);

  const handleDolphinScenario = async (profile) => {
    if (!profile.dolphin_profile_id) {
      toast.error("Profile not exported to Dolphin. Export first!");
      return;
    }
    setDolphinRunning(profile.id);
    try {
      const res = await axios.post(`${API}/dolphin-anty/run-scenario`, {
        profile_id: profile.id,
        template_type: "cashinout",
      });
      if (!res.data.success) {
        toast.error(typeof res.data.error === 'string' ? res.data.error : "Error");
        setDolphinRunning(null);
        return;
      }
      toast.info("Dolphin scenario started...", { duration: 3000 });

      // Poll for status
      const pollInterval = 5000;
      const maxPolls = 60;
      let polls = 0;
      const poll = async () => {
        polls++;
        try {
          const statusRes = await axios.get(`${API}/dolphin-anty/scenario-status/${profile.id}`);
          const { status, steps } = statusRes.data;
          const lastStep = steps?.[steps.length - 1] || "";

          if (status === "done") {
            toast.success(`Dolphin: done! (${steps.length} steps)`);
            setDolphinRunning(null);
            fetchProfiles();
            return;
          }
          if (status === "error") {
            toast.error(`Dolphin error: ${lastStep}`);
            setDolphinRunning(null);
            return;
          }
          if (polls >= maxPolls) {
            toast.error("Dolphin: timeout");
            setDolphinRunning(null);
            return;
          }
          toast.info(`Dolphin: ${lastStep}`, { id: "dolphin-progress", duration: pollInterval + 1000 });
          setTimeout(poll, pollInterval);
        } catch {
          if (polls < maxPolls) setTimeout(poll, pollInterval);
          else { toast.error("Dolphin: connection error"); setDolphinRunning(null); }
        }
      };
      setTimeout(poll, pollInterval);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Dolphin error");
      setDolphinRunning(null);
    }
  };

  const handleDuplicate = async (profileId) => {
    try {
      await axios.post(`${API}/profiles/${profileId}/duplicate`);
      toast.success("Профиль скопирован");
      fetchProfiles();
      refreshFolders?.();
    } catch (e) {
      toast.error("Ошибка копирования");
    }
  };

  const handleDownloadArchive = async (profileId) => {
    try {
      toast.info("Генерация архива...", { id: "archive-dl" });
      const res = await axios.post(`${API}/profiles/${profileId}/cashinout-archive`);
      if (res.data.archive) {
        const byteChars = atob(res.data.archive);
        const byteNumbers = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteNumbers], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data.filename || "cashinout.zip";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Архив скачан", { id: "archive-dl" });
      }
    } catch (e) {
      toast.error("Ошибка генерации архива", { id: "archive-dl" });
    }
  };

  const [proxyChecking, setProxyChecking] = useState(null);
  const handleCheckProxy = async (profileId) => {
    setProxyChecking(profileId);
    try {
      const res = await axios.post(`${API}/profiles/${profileId}/check-proxy`);
      if (res.data.working) {
        toast.success(`Прокси ОК (${res.data.ip || "?"})`, { duration: 3000 });
      } else {
        toast.error(`Прокси не работает: ${res.data.error || "?"}`, { duration: 4000 });
      }
      fetchProfiles();
    } catch (e) {
      toast.error("Ошибка проверки прокси");
    } finally {
      setProxyChecking(null);
    }
  };

  const handleEditProxy = async (profileId, proxyString) => {
    let username = "", password = "", host = "", port = 0;
    const raw = proxyString.trim();
    if (raw.includes("@")) {
      const [auth, server] = [raw.substring(0, raw.lastIndexOf("@")), raw.substring(raw.lastIndexOf("@") + 1)];
      const authParts = auth.split(":");
      username = authParts[0] || "";
      password = authParts.slice(1).join(":") || "";
      const serverParts = server.split(":");
      host = serverParts[0] || "";
      port = parseInt(serverParts[1]) || 0;
    } else {
      const parts = raw.split(":");
      if (parts.length === 4) {
        host = parts[0]; port = parseInt(parts[1]) || 0;
        username = parts[2]; password = parts[3];
      } else if (parts.length === 2) {
        host = parts[0]; port = parseInt(parts[1]) || 0;
      }
    }
    if (!host || !port) { toast.error("Неверный формат прокси"); return; }
    try {
      await axios.put(`${API}/profiles/${profileId}`, {
        proxy: { enabled: true, type: "http", host, port, username, password }
      });
      toast.success(`Прокси: ${host}:${port}`);
      fetchProfiles();
    } catch (e) {
      toast.error("Ошибка обновления прокси");
    }
  };

  const handleDelete = async () => {
    if (profileToDelete) {
      // Single delete
      try {
        await axios.delete(`${API}/profiles/${profileToDelete}`);
        toast.success("Профиль удалён");
        fetchProfiles();
        refreshFolders?.();
      } catch (e) {
        toast.error("Ошибка удаления");
      }
    } else if (selectedIds.length > 0) {
      // Mass delete
      await handleMassDelete();
    }
    setDeleteDialogOpen(false);
    setProfileToDelete(null);
  };

  const handleMoveToFolder = async (profileId, folderId) => {
    try {
      await axios.put(`${API}/profiles/${profileId}`, { folder_id: folderId });
      toast.success("Профиль перемещён");
      fetchProfiles();
      refreshFolders?.();
    } catch (e) {
      toast.error("Ошибка перемещения");
    }
  };

  const handleChangeStatus = async (profileId, customStatusId) => {
    try {
      await axios.put(`${API}/profiles/${profileId}/status`, {
        custom_status: customStatusId,
      });
      fetchProfiles();
    } catch (e) {
      toast.error("Ошибка смены статуса");
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === displayedProfiles.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(displayedProfiles.map((p) => p.id));
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const getPlatformIcon = (platform) => {
    if (platform?.includes("Win")) return <WindowsLogo size={14} className="text-[#00A4EF] shrink-0" />;
    if (platform?.includes("Mac")) return <AppleLogo size={14} className="text-[#A2AAAD] shrink-0" />;
    if (platform?.includes("Linux")) return <LinuxLogo size={14} className="text-[#FCC624] shrink-0" />;
    return <Globe size={14} className="text-[#5C6370] shrink-0" />;
  };

  const getStatusDisplay = (profile) => {
    if (profile.custom_status) {
      const cs = customStatuses.find((s) => s.id === profile.custom_status);
      if (cs) {
        return (
          <span className="status-badge" style={{ background: cs.color, color: "#fff" }}>
            {cs.name}
          </span>
        );
      }
    }
    switch (profile.status) {
      case "running":
        return <span className="status-badge status-running">Запущен</span>;
      case "error":
        return <span className="status-badge status-error">Ошибка</span>;
      case "no_pin":
        return <span className="status-badge status-no-pin">NO PIN</span>;
      case "no_block":
        return <span className="status-badge status-no-block">NO BLOCK</span>;
      case "arrest":
        return <span className="status-badge status-arrest">АРЕСТ</span>;
      case "close_account":
        return <span className="status-badge status-close-account">ЗАКРЫТЬ СЧЁТ</span>;
      default:
        return <span className="status-badge status-rest">REST</span>;
    }
  };

  const getFolderName = (folderId) => {
    if (!folderId) return "—";
    const folder = folders.find((f) => f.id === folderId);
    return folder ? folder.name : "—";
  };

  const getFolderColor = (folderId) => {
    if (!folderId) return "#5C6370";
    const folder = folders.find((f) => f.id === folderId);
    return folder ? folder.color : "#5C6370";
  };

  const displayedProfiles = profiles
    .filter((p) => !selectedFolderId || p.folder_id === selectedFolderId)
    .filter(
      (p) =>
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.ozon?.fullname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.ozon?.card?.includes(searchQuery)
    );

  const currentFolder = folders.find((f) => f.id === selectedFolderId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <ArrowsClockwise size={24} className="animate-spin text-[#2196F3]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="profiles-table-page">
      {/* Top bar */}
      <div className="top-bar" style={{ flexShrink: 0 }}>
        <div className="top-bar-left">
          <div className="folder-title">
            <div className="folder-icon" style={{ background: currentFolder?.color || "#2196F3" }}>
              <Folder size={14} weight="fill" className="text-white" />
            </div>
            <span>{currentFolder?.name || "Все профили"}</span>
            <span className="text-sm text-[#5C6370] ml-2">({displayedProfiles.length})</span>
          </div>
        </div>

        <div className="top-bar-right">
          {selectedIds.length > 0 && (
            <>
              <button
                onClick={handleMassLaunch}
                disabled={massLaunching}
                className="action-btn action-btn-success"
                data-testid="mass-launch-btn"
              >
                {massLaunching ? (
                  <ArrowsClockwise size={14} className="animate-spin" />
                ) : (
                  <Rocket size={14} weight="bold" />
                )}
                Запустить ({selectedIds.length})
              </button>
              <button
                onClick={handleMassStop}
                className="action-btn action-btn-danger"
                data-testid="mass-stop-btn"
              >
                <Stop size={14} weight="bold" />
                Стоп ({selectedIds.length})
              </button>
              <button
                onClick={() => setDeleteDialogOpen(true)}
                className="action-btn action-btn-danger"
                style={{ background: "#7B1FA2" }}
                data-testid="mass-delete-btn"
              >
                <Trash size={14} weight="bold" />
                Удалить ({selectedIds.length})
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="action-btn"
                    style={{ background: "#FF9800", color: "#fff" }}
                    data-testid="mass-move-btn"
                  >
                    <Folder size={14} weight="bold" />
                    Папка ({selectedIds.length})
                    <CaretDown size={12} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-[#1A1D24] border-[#252A33] min-w-[180px]">
                  <DropdownMenuItem
                    onClick={() => handleMassMove(null)}
                    className="text-xs cursor-pointer"
                  >
                    Без папки
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {folders.map(f => (
                    <DropdownMenuItem
                      key={f.id}
                      onClick={() => handleMassMove(f.id)}
                      className="text-xs cursor-pointer"
                    >
                      <div className="w-2 h-2 rounded-full mr-2" style={{ background: f.color || "#5C6370" }} />
                      {f.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu onOpenChange={(open) => { if (open) fetchDolphinFolders(); }}>
                <DropdownMenuTrigger asChild>
                  <button
                    disabled={exportingDolphin}
                    className="action-btn"
                    style={{ background: "#0288D1", color: "#fff" }}
                    data-testid="export-dolphin-btn"
                  >
                    {exportingDolphin ? (
                      <CircleNotch size={14} className="animate-spin" />
                    ) : (
                      <DownloadSimple size={14} weight="bold" />
                    )}
                    Dolphin ({selectedIds.length})
                    <CaretDown size={12} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-[#1A1D24] border-[#252A33] min-w-[200px]">
                  <DropdownMenuItem
                    onClick={() => handleExportDolphin(null)}
                    className="text-xs cursor-pointer"
                  >
                    Без папки
                  </DropdownMenuItem>
                  {dolphinFolders.length > 0 && <DropdownMenuSeparator />}
                  {dolphinFolders.map(f => (
                    <DropdownMenuItem
                      key={f.id}
                      onClick={() => handleExportDolphin(f.id)}
                      className="text-xs cursor-pointer"
                    >
                      <Folder size={14} className="mr-2 text-[#FF9800]" />
                      {f.name}
                    </DropdownMenuItem>
                  ))}
                  {!dolphinFoldersLoaded && (
                    <DropdownMenuItem disabled className="text-xs text-[#5C6370]">
                      <CircleNotch size={12} className="mr-2 animate-spin" /> Loading...
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="h-6 w-px bg-[#252A33]" />
            </>
          )}

          <Link to={`/profiles/new${selectedFolderId ? `?folder=${selectedFolderId}` : ""}`}>
            <button className="action-btn action-btn-primary" data-testid="create-profile-btn">
              <Plus size={14} weight="bold" />
              Создать
            </button>
          </Link>
          <button className="action-btn action-btn-outline" data-testid="quick-profile-btn">
            <Lightning size={14} weight="bold" />
            Быстрый
          </button>

          <div className="h-6 w-px bg-[#252A33]" />

          <div className="search-box">
            <MagnifyingGlass size={16} />
            <input
              type="text"
              placeholder="Поиск..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
            />
          </div>

          <button className="icon-btn" onClick={fetchProfiles} data-testid="refresh-btn">
            <ArrowsClockwise size={18} />
          </button>
          <button className="icon-btn" data-testid="filter-btn">
            <Funnel size={18} />
          </button>
        </div>
      </div>

      {/* Telegram banner - only show when NOT connected */}
      {!telegramConnected && !selectedFolderId && (
        <div className="px-5 pt-3" style={{ flexShrink: 0 }}>
          <div className="info-banner">
            <Info size={18} className="text-[#2196F3] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium">Подключите Telegram для импорта</p>
            </div>
            <Link to="/telegram">
              <button className="action-btn action-btn-outline text-[#2196F3] border-[#2196F3] text-xs">
                Подключить
              </button>
            </Link>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="content-area" style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {displayedProfiles.length === 0 ? (
          <div className="empty-state">
            <Globe size={48} className="empty-state-icon" />
            <div className="empty-state-title">Нет профилей</div>
            <p className="empty-state-text">
              {selectedFolderId ? "В этой папке пока нет профилей" : "Создайте профиль или импортируйте из Telegram"}
            </p>
            <Link to={`/profiles/new${selectedFolderId ? `?folder=${selectedFolderId}` : ""}`} className="mt-4">
              <button className="action-btn action-btn-primary">
                <Plus size={14} />
                Создать профиль
              </button>
            </Link>
          </div>
        ) : (
          <table className="profile-table" style={{ tableLayout: "fixed", width: "100%" }} data-testid="profiles-table">
            <colgroup>
              <col style={{ width: "36px" }} />
              <col style={{ width: "60px" }} />
              <col style={{ width: "26%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "36px" }} />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <button onClick={handleSelectAll} className="custom-checkbox" data-testid="select-all">
                    {selectedIds.length === displayedProfiles.length && selectedIds.length > 0 ? (
                      <CheckSquare size={14} weight="fill" className="text-white" />
                    ) : null}
                  </button>
                </th>
                <th></th>
                <th>Название</th>
                <th>Баланс</th>
                <th>Папка</th>
                <th>Статус</th>
                <th>Прокси</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayedProfiles.map((profile, idx) => (
                <ProfileRow
                  key={profile.id}
                  profile={profile}
                  idx={idx}
                  selected={selectedIds.includes(profile.id)}
                  onSelect={handleSelect}
                  onLaunch={handleLaunch}
                  onStop={handleStop}
                  onView={setViewerProfile}
                  onSupport={handleSupportScenario}
                  onDolphinScenario={handleDolphinScenario}
                  onDuplicate={handleDuplicate}
                  onDownloadArchive={handleDownloadArchive}
                  onDelete={(id) => { setProfileToDelete(id); setDeleteDialogOpen(true); }}
                  onMove={handleMoveToFolder}
                  onStatusChange={handleChangeStatus}
                  onInfo={setInfoProfile}
                  onCheckProxy={handleCheckProxy}
                  onEditProxy={handleEditProxy}
                  proxyChecking={proxyChecking}
                  launchingId={launchingId}
                  supportRunning={supportRunning}
                  dolphinRunning={dolphinRunning}
                  folders={folders}
                  customStatuses={customStatuses}
                  getPlatformIcon={getPlatformIcon}
                  getStatusDisplay={getStatusDisplay}
                  getFolderName={getFolderName}
                  getFolderColor={getFolderColor}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {displayedProfiles.length > 0 && (
        <div className="pagination" style={{ flexShrink: 0 }}>
          <span>Записей: {displayedProfiles.length}</span>
          <div className="pagination-controls">
            <span>1 - {displayedProfiles.length} из {displayedProfiles.length}</span>
            <button className="icon-btn" disabled><CaretLeft size={16} /></button>
            <button className="icon-btn" disabled><CaretRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Delete dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1A1D24] border-[#252A33]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {profileToDelete ? "Удалить профиль?" : `Удалить ${selectedIds.length} профилей?`}
            </AlertDialogTitle>
            <AlertDialogDescription>Это действие нельзя отменить.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#252A33]">Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OzonInfoModal profile={infoProfile} onClose={() => setInfoProfile(null)} />

      {viewerProfile && (
        <BrowserViewer
          profileId={viewerProfile.id}
          profileName={viewerProfile.name}
          profileOzon={viewerProfile.ozon}
          onClose={() => setViewerProfile(null)}
        />
      )}
    </div>
  );
};

/* Proxy cell with check arrows and inline edit */
const ProxyCell = ({ profile, onCheckProxy, onEditProxy, proxyChecking }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  if (!profile.proxy?.enabled) {
    return <span className="text-[#5C6370] text-[11px]">—</span>;
  }

  const p = profile.proxy;
  const proxyStr = `${p.type}://${p.host}:${p.port}:${p.username}:${p.password}`;
  const isChecking = proxyChecking === profile.id;
  const statusColor = profile.proxy_status === "ok" ? "#4CAF50" : profile.proxy_status === "error" ? "#F44336" : "#5C6370";

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          defaultValue={`${p.username}:${p.password}@${p.host}:${p.port}`}
          className="bg-[#0D0F12] border border-[#252A33] text-[11px] font-mono text-[#ccc] px-1.5 py-0.5 rounded w-[200px] outline-none focus:border-[#1A5CFF]"
          data-testid={`proxy-edit-input-${profile.id}`}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onEditProxy(profile.id, e.target.value);
              setEditing(false);
            }
            if (e.key === "Escape") setEditing(false);
          }}
          onBlur={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 min-w-0">
      <button
        onClick={() => onCheckProxy(profile.id)}
        className="shrink-0 hover:text-[#2196F3] transition-colors"
        disabled={isChecking}
        title="Проверить прокси"
        data-testid={`proxy-check-btn-${profile.id}`}
      >
        {isChecking
          ? <CircleNotch size={14} className="text-[#2196F3] animate-spin" />
          : <ArrowsLeftRight size={14} style={{ color: statusColor }} />
        }
      </button>
      <span
        className="font-mono text-[11px] text-[#8A9099] truncate max-w-[180px] cursor-default"
        title={proxyStr}
      >
        {proxyStr}
      </span>
      <button
        onClick={() => setEditing(true)}
        className="shrink-0 hover:text-[#FF9800] transition-colors text-[#5C6370]"
        title="Изменить прокси"
        data-testid={`proxy-edit-btn-${profile.id}`}
      >
        <PencilSimpleLine size={12} />
      </button>
    </div>
  );
};

/* Compact profile row component */
const ProfileRow = ({
  profile, idx, selected, onSelect, onLaunch, onStop, onView, onSupport, onDolphinScenario, onDuplicate, onDownloadArchive, onDelete,
  onMove, onStatusChange, onInfo, onCheckProxy, onEditProxy, launchingId, supportRunning, dolphinRunning, folders, customStatuses,
  getPlatformIcon, getStatusDisplay, getFolderName, getFolderColor, proxyChecking,
}) => (
  <tr
    className={`animate-row ${selected ? "selected" : ""}`}
    style={{ animationDelay: `${idx * 0.02}s` }}
    data-testid={`profile-row-${profile.id}`}
  >
    <td>
      <button
        onClick={() => onSelect(profile.id)}
        className={`custom-checkbox ${selected ? "checked" : ""}`}
      >
        {selected && <CheckSquare size={14} weight="fill" className="text-white" />}
      </button>
    </td>
    <td>
      <div className="flex items-center gap-1">
        {profile.status === "running" ? (
          <>
            <button
              onClick={() => onStop(profile)}
              className="w-6 h-6 rounded flex items-center justify-center bg-[#F44336] hover:bg-[#D32F2F] transition-colors"
              data-testid={`stop-btn-${profile.id}`}
              title="Стоп"
            >
              <Stop size={11} weight="fill" className="text-white" />
            </button>
            <button
              onClick={() => onView(profile)}
              className="w-6 h-6 rounded flex items-center justify-center bg-[#2196F3] hover:bg-[#1976D2] transition-colors"
              data-testid={`view-btn-${profile.id}`}
              title="Смотреть браузер"
            >
              <Globe size={11} weight="bold" className="text-white" />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => onLaunch(profile)}
              disabled={launchingId === profile.id}
              className="w-6 h-6 rounded flex items-center justify-center bg-[#4CAF50] hover:bg-[#388E3C] transition-colors disabled:opacity-50"
              data-testid={`launch-btn-${profile.id}`}
              title="Запуск"
            >
              {launchingId === profile.id ? (
                <ArrowsClockwise size={11} className="text-white animate-spin" />
              ) : (
                <Play size={11} weight="fill" className="text-white" />
              )}
            </button>
            <button
              onClick={() => onSupport(profile)}
              disabled={supportRunning === profile.id}
              className="w-6 h-6 rounded flex items-center justify-center bg-[#FF9800] hover:bg-[#F57C00] transition-colors disabled:opacity-50"
              data-testid={`support-btn-${profile.id}`}
              title="Cashinout"
            >
              {supportRunning === profile.id ? (
                <ArrowsClockwise size={11} className="text-white animate-spin" />
              ) : (
                <ChatCircleText size={11} weight="bold" className="text-white" />
              )}
            </button>
            {profile.dolphin_profile_id && (
              <button
                onClick={() => onDolphinScenario(profile)}
                disabled={dolphinRunning === profile.id}
                className="w-6 h-6 rounded flex items-center justify-center bg-[#0288D1] hover:bg-[#0277BD] transition-colors disabled:opacity-50"
                data-testid={`dolphin-scenario-btn-${profile.id}`}
                title="Dolphin Scenario"
              >
                {dolphinRunning === profile.id ? (
                  <ArrowsClockwise size={11} className="text-white animate-spin" />
                ) : (
                  <Globe size={11} weight="bold" className="text-white" />
                )}
              </button>
            )}
            {(profile.cookies?.length > 0) && (
              <a
                href={`${API}/profiles/${profile.id}/export-cookies`}
                download
                className="w-6 h-6 rounded flex items-center justify-center bg-[#FF9800] hover:bg-[#F57C00] transition-colors"
                data-testid={`export-cookies-btn-${profile.id}`}
                title="Download cookies for Dolphin"
              >
                <DownloadSimple size={11} weight="bold" className="text-white" />
              </a>
            )}
          </>
        )}
      </div>
    </td>
    <td>
      <div className="flex items-center gap-1.5 min-w-0">
        {getPlatformIcon(profile.fingerprint?.platform)}
        <span className="truncate text-sm font-medium" title={profile.name}>{profile.name}</span>
        {profile.ozon?.fullname && (
          <span className="text-[10px] text-[#5C6370] truncate hidden xl:inline">({profile.ozon.fullname})</span>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="icon-btn context-menu-btn ml-auto shrink-0 w-6 h-6" data-testid={`menu-btn-${profile.id}`}>
              <DotsThree size={16} weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem asChild>
              <Link to={`/profiles/${profile.id}`} className="flex items-center gap-2">
                <PencilSimple size={14} />
                Редактировать
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDuplicate(profile.id)}>
              <Copy size={14} className="mr-2" />
              Копировать
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDownloadArchive(profile.id)} data-testid={`download-archive-${profile.id}`}>
              <DownloadSimple size={14} className="mr-2" />
              Скачать архив шаблона
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <FolderOpen size={14} className="mr-2" />
                В папку
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => onMove(profile.id, null)}>
                  <Folder size={14} className="mr-2 text-[#5C6370]" />
                  Основная
                </DropdownMenuItem>
                {folders.map((f) => (
                  <DropdownMenuItem key={f.id} onClick={() => onMove(profile.id, f.id)}>
                    <Folder size={14} className="mr-2" style={{ color: f.color }} />
                    {f.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(profile.id)} className="text-red-400">
              <Trash size={14} className="mr-2" />
              Удалить
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </td>
    <td>
      <span className="font-mono text-[11px] whitespace-nowrap">
        {profile.ozon?.balance ? `${profile.ozon.balance} ₽` : "—"}
      </span>
    </td>
    <td>
      <div className="flex items-center gap-1 min-w-0">
        <Folder size={12} className="shrink-0" style={{ color: getFolderColor(profile.folder_id) }} />
        <span className="text-[#8A9099] text-[11px] truncate">{getFolderName(profile.folder_id)}</span>
      </div>
    </td>
    <td>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-0.5 cursor-pointer hover:opacity-80 transition-opacity" data-testid={`status-trigger-${profile.id}`}>
            {getStatusDisplay(profile)}
            <CaretDown size={9} className="text-[#5C6370]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-40">
          <DropdownMenuItem onClick={() => onStatusChange(profile.id, "")}>
            <span className="w-2.5 h-2.5 rounded-full bg-[#2196F3] mr-2" />
            REST
          </DropdownMenuItem>
          {customStatuses.map((cs) => (
            <DropdownMenuItem key={cs.id} onClick={() => onStatusChange(profile.id, cs.id)}>
              <span className="w-2.5 h-2.5 rounded-full mr-2" style={{ background: cs.color }} />
              {cs.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </td>
    <td>
      <ProxyCell
        profile={profile}
        onCheckProxy={onCheckProxy}
        onEditProxy={onEditProxy}
        proxyChecking={proxyChecking}
      />
    </td>
    <td>
      <button
        onClick={() => onInfo(profile)}
        className="icon-btn w-6 h-6 hover:text-[#2196F3]"
        data-testid={`info-btn-${profile.id}`}
        title="Информация"
      >
        <CreditCard size={15} weight="duotone" />
      </button>
    </td>
  </tr>
);

const OzonInfoModal = ({ profile, onClose }) => {
  if (!profile) return null;
  const ozon = profile.ozon;

  return (
    <Dialog open={!!profile} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-[#1A1D24] border-[#252A33] max-w-lg" data-testid="ozon-info-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard size={20} weight="duotone" className="text-[#2196F3]" />
            Информация — {profile.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <InfoField label="Телефон" value={profile.name} mono />
            <InfoField label="ФИО" value={ozon?.fullname || "—"} />
            <InfoField label="Карта" value={ozon?.card || "—"} mono />
            <InfoField label="Пинкод" value={ozon?.pincode || "—"} mono highlight />
            <InfoField label="Баланс" value={ozon?.balance ? `${ozon.balance} ₽` : "—"} highlight />
            <InfoField label="Лимит вывода" value={ozon?.withdraw_limit ? `${ozon.withdraw_limit} ₽` : "—"} />
            <InfoField label="Всего оплат" value={ozon?.total_payments ? `${ozon.total_payments} ₽` : "—"} />
            <InfoField label="Покупки" value={ozon?.buy_counter?.toString() || "0"} />
            <InfoField label="Месячные расходы" value={ozon?.monthly_spendings ? `${ozon.monthly_spendings} ₽` : "—"} />
            <InfoField label="На паузе" value={ozon?.on_pause ? "Да" : "Нет"} />
          </div>

          {ozon?.last_observed_transaction && (
            <div className="bg-[#0D0F12] rounded p-3 border border-[#252A33]">
              <div className="text-[11px] font-semibold text-[#5C6370] uppercase tracking-wider mb-2">
                Последняя транзакция
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-[#5C6370]">Сумма: </span>
                  <span className="font-mono text-[#4CAF50]">{ozon.last_observed_transaction.amount} ₽</span>
                </div>
                <div>
                  <span className="text-[#5C6370]">Время: </span>
                  <span className="font-mono">{formatDate(ozon.last_observed_transaction.time)}</span>
                </div>
              </div>
            </div>
          )}

          {ozon?.withdraw_stats && (
            <div className="bg-[#0D0F12] rounded p-3 border border-[#252A33]">
              <div className="text-[11px] font-semibold text-[#5C6370] uppercase tracking-wider mb-2">
                Статистика выводов
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-[#5C6370]">Выводы: </span><span className="font-mono">{ozon.withdraw_stats.withdraws} ₽</span></div>
                <div><span className="text-[#5C6370]">Покупки: </span><span className="font-mono">{ozon.withdraw_stats.purchases} ₽</span></div>
                <div><span className="text-[#5C6370]">Переводы: </span><span className="font-mono">{ozon.withdraw_stats.transfers} ₽</span></div>
              </div>
            </div>
          )}

          {profile.proxy?.enabled && (
            <div className="bg-[#0D0F12] rounded p-3 border border-[#252A33]">
              <div className="text-[11px] font-semibold text-[#5C6370] uppercase tracking-wider mb-2">Прокси</div>
              <div className="font-mono text-xs break-all">
                {profile.proxy.type}://{profile.proxy.username}:{profile.proxy.password}@{profile.proxy.host}:{profile.proxy.port}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-[#5C6370] pt-1">
            <span>Куки: {profile.cookies?.length || 0}</span>
            <span>Источник: {profile.source}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InfoField = ({ label, value, mono, highlight }) => (
  <div className="bg-[#0D0F12] rounded p-2.5 border border-[#252A33]" data-testid={`info-field-${label}`}>
    <div className="text-[10px] text-[#5C6370] uppercase tracking-wider mb-1">{label}</div>
    <div className={`text-sm ${mono ? "font-mono" : ""} ${highlight ? "text-[#FACC15]" : ""}`}>
      {value}
    </div>
  </div>
);

const formatDate = (isoStr) => {
  if (!isoStr) return "—";
  try {
    const d = new Date(isoStr);
    return d.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return isoStr;
  }
};

export default ProfilesTable;
